import { useMemo, useState } from 'react';
import { useResizeObserver } from 'usehooks-ts';

/**
 * Fallback height in CSS pixels, used only until the observed node has been measured once.
 * It is the value the header has always used when no measurement was available.
 */
export const DEFAULT_METRICS_HEIGHT = 90;

/**
 * Measures the border-box height of a node that may mount late or remount.
 *
 * `useResizeObserver` (usehooks-ts) hangs its effect on `[box, ref, isMounted]`; a plain
 * `useRef` keeps its identity for the lifetime of the component, so the effect runs once
 * and only ever observes the node that existed at that moment. A node mounted later — or
 * remounted after a route hid it — is never observed and the reported height stays stale
 * (or at the fallback), which collapses the spacer below the detached metrics.
 *
 * The node therefore lives in state and the ref object is derived from it: the ref identity
 * changes exactly when the node does, which re-runs the observer effect against the new node
 * and disconnects the old one. A callback ref (`ref={metricsRef}`) feeds the state; React
 * calls it with `null` on unmount and again with the node under StrictMode's double invoke.
 *
 * Returns the callback ref to attach and the measured border-box height in pixels.
 */
const useMetricsNodeHeight = (fallback = DEFAULT_METRICS_HEIGHT) => {
    const [metricsNode, setMetricsNode] = useState(null);
    const metricsRef = useMemo(() => ({ current: metricsNode }), [metricsNode]);
    const { height } = useResizeObserver({ ref: metricsRef, box: 'border-box' });

    return { metricsRef: setMetricsNode, metricsHeight: height === undefined ? fallback : height };
};

export default useMetricsNodeHeight;
